import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaEllipsisH,
  FaTimes,
  FaUserPlus,
  FaLaptop,
  FaDesktop,
  FaMobileAlt,
  FaTabletAlt,
  FaHistory,
  FaUserLock,
  FaBolt,
  FaClock,
  FaFileExcel,
  FaDownload,
  FaMapMarkerAlt,
  FaBuilding,
  FaUser,
  FaExchangeAlt,
  FaNetworkWired,
  FaAddressCard,
  FaSignature,
  FaHandshake,
  FaPlayCircle,
  FaClipboardList,
} from "react-icons/fa";
import TableView from "./TableView";
import ExpedienteEmpleado from "./ExpedienteEmpleado";
import NewEmployeeModal from "./NewEmployeeModal";
import AssetModal from "./AssetModal";
import SmartphoneModal from "./SmartphoneModal";
import TabletModal from "./TabletModal";
import TimelineView from "./TimelineView";
import CuentasModal from "./CuentasModal";
import MovimientoInternoModal from "./MovimientoInternoModal";
import GenerarFirmaModal from "./GenerarFirmaModal";
import GenerarBienvenidaModal from "./GenerarBienvenidaModal";
import DeleteEmployeeModal from "./DeleteEmployeeModal";
import CierreCuentasModal from "./CierreCuentas";
import FichaITSModal from "./FichaITSModal";
import OnboardingWizard from "./OnboardingWizard";
import EmployeePhotoModal from "./EmployeePhotoModal";
import EditNameModal from "./EditNameModal";
import { QuickActionsMenu } from "./common";
import "./EmployeesView.css";
import axiosInstance from "../utils/axiosConfig";
import { useAuth } from "../context/AuthContext";
import { estadoFicha, fechaCorta, textoDias } from "../utils/fichaTi";

// Componente para celdas editables
const EditableCell = ({ value, column, row, onSave, hidden = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (column.id === "fecha_ingreso" && value) {
      // Extraer solo la parte YYYY-MM-DD de la fecha
      const fecha = value.split('T')[0];
      setCurrentValue(fecha);
    } else {
      setCurrentValue(value);
    }
  }, [value, column.id]);

  const fetchOptions = async (field) => {
    setLoading(true);
    try {
      let response;
      switch (field) {
        case "sede":
          response = await axiosInstance.get("/api/sedes");
          setOptions(response.data);
          break;
        case "gerencia":
          response = await axiosInstance.get("/api/gerencias");
          setOptions(response.data);
          break;
        case "departamento":
          if (row.original.gerencia_id) {
            response = await axiosInstance.get(
              `/api/departamentos/${row.original.gerencia_id}`
            );
            setOptions(response.data);
          }
          break;
        case "area":
          if (row.original.departamento_id) {
            response = await axiosInstance.get(
              `/api/areas/${row.original.departamento_id}`
            );
            setOptions(response.data);
          }
          break;
        case "cargo":
          if (row.original.area_id) {
            response = await axiosInstance.get(
              `/api/cargos/${row.original.area_id}`
            );
            setOptions(response.data);
          }
          break;
      }
    } catch (error) {
      console.error("Error loading options:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDoubleClick = () => {
    if (
      ["sede", "gerencia", "departamento", "area", "cargo"].includes(column.id)
    ) {
      fetchOptions(column.id);
    }
    setIsEditing(true);
  };

  const handleInputChange = (e) => {
    setCurrentValue(e.target.value);
  };

  const handleChange = async (e) => {
    const newValue = e.target.value;
    if (
      ["sede", "gerencia", "departamento", "area", "cargo"].includes(column.id)
    ) {
      const selectedOption = options.find(
        (opt) => opt.id.toString() === newValue
      );
      const success = await onSave(
        row.original.id,
        column.id + "_id",
        selectedOption.id,
        selectedOption.nombre
      );
      if (success) {
        setCurrentValue(selectedOption.nombre);
      } else {
        setCurrentValue(value);
      }
    } else {
      const success = await onSave(row.original.id, column.id, newValue);
      if (success) {
        setCurrentValue(newValue);
      } else {
        setCurrentValue(value);
      }
    }
    setIsEditing(false);
  };

  if (isEditing) {
    if (
      ["sede", "gerencia", "departamento", "area", "cargo"].includes(column.id)
    ) {
      return (
        <select
          value={currentValue || ""}
          onChange={handleChange}
          onBlur={() => setIsEditing(false)}
          autoFocus
          className="editable-cell-input"
          disabled={loading}
        >
          <option value="">Seleccione una opción</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.nombre}
            </option>
          ))}
        </select>
      );
    }

    if (column.id === "fecha_ingreso") {
      return (
        <input
          type="date"
          value={currentValue || ''}
          onChange={handleInputChange}
          onBlur={handleChange}
          autoFocus
          className="editable-cell-input"
        />
      );
    }

    return (
      <input
        type="text"
        value={currentValue || ""}
        onChange={handleInputChange}
        onBlur={handleChange}
        autoFocus
        className="editable-cell-input"
      />
    );
  }

  if (column.id === "fecha_ingreso") {
    const formatearFecha = (fechaStr) => {
      if (!fechaStr) return 'No registrada';
      // Si la fecha viene con formato largo (Mon, 31 Mar 2025...), extraer solo la fecha
      const fecha = fechaStr.includes(',') ?
        fechaStr.split(',')[1].trim().split(' ').slice(0, 3) : // ['31', 'Mar', '2025']
        fechaStr.split('T')[0].split('-'); // ['2025', '03', '31']

      if (fecha.length === 3) {
        if (fecha[1].length > 2) {
          // Convertir mes en texto a número
          const meses = {
            'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'May': '05', 'Jun': '06',
            'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
          };
          return `${fecha[0].padStart(2, '0')}/${meses[fecha[1]]}/${fecha[2]}`;
        } else {
          // Ya está en formato YYYY-MM-DD
          return `${fecha[2].padStart(2, '0')}/${fecha[1].padStart(2, '0')}/${fecha[0]}`;
        }
      }
      return fechaStr;
    };

    return (
      <div onDoubleClick={handleDoubleClick} className="editable-cell">
        {formatearFecha(value)}
      </div>
    );
  }

  if (hidden) {
    return (
      <div
        onDoubleClick={handleDoubleClick}
        className="editable-cell hidden-editable"
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
      >
        {value || "\u00A0"}
      </div>
    );
  }

  return (
    <div onDoubleClick={handleDoubleClick} className="editable-cell">
      {value || "\u00A0"}
    </div>
  );
};

function EmployeesView() {
  const { user } = useAuth();
  // El wizard de Onboarding TI crea cuentas: solo admin y TI
  const puedeCorrerWizard = ["admin", "ti"].includes(user?.role);
  // Ascensos y traslados mueven gente entre vacantes: los que gestionan personal
  const puedeMoverPersonal = ["admin", "gh", "captacion"].includes(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewEmployeeModal, setShowNewEmployeeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showSmartphoneModal, setShowSmartphoneModal] = useState(false);
  const [showTabletModal, setShowTabletModal] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [employeeForApproval, setEmployeeForApproval] = useState(null);
  const [employeeForWizard, setEmployeeForWizard] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [columnVisibility, setColumnVisibility] = useState({
    sede: true,
    ficha: true,
    foto: true,
    cedula: true,
    nombre: true,
    gerencia: true,
    departamento: true,
    area: true,
    cargo: true,
    extension: false,
    linea: false,
    correo: false,
    fecha_ingreso: false,
    equipo_asignado: false,
    smartphone_asignado: false,
    tablet_asignada: false,
    acciones: true,
  });
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [cuentaRedDe, setCuentaRedDe] = useState(null);
  // Cierre de cuentas de un egresado o de la silla de quien cambió de puesto: { id, tipo }
  const [cierreDe, setCierreDe] = useState(null);
  const [movimientoDe, setMovimientoDe] = useState(null);
  const [showGenerarFirmaModal, setShowGenerarFirmaModal] = useState(false);
  const [selectedEmployeeForFirma, setSelectedEmployeeForFirma] =
    useState(null);
  const [showGenerarBienvenidaModal, setShowGenerarBienvenidaModal] =
    useState(false);
  const [selectedEmployeeForBienvenida, setSelectedEmployeeForBienvenida] =
    useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedEmployeeForPhoto, setSelectedEmployeeForPhoto] = useState(null);

  // Estados faltantes restaurados
  const [filters, setFilters] = useState({});
  const [filteredCount, setFilteredCount] = useState(0);
  const [filteredRows, setFilteredRows] = useState([]);
  const [gerencias, setGerencias] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedEmployeeForDelete, setSelectedEmployeeForDelete] = useState(null);
  const [showIndividualTimeline, setShowIndividualTimeline] = useState(false);
  const [selectedEmployeeForTimeline, setSelectedEmployeeForTimeline] = useState(null);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [selectedEmployeeForNameEdit, setSelectedEmployeeForNameEdit] = useState(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const quickActionsRef = useRef(null);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Agregar useEffect para manejar clics fuera del menú
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        activeActionMenu &&
        quickActionsRef.current &&
        !quickActionsRef.current.contains(event.target) &&
        !event.target.closest('.icon-button') &&
        !event.target.closest('.quick-actions-menu')
      ) {
        setActiveActionMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeActionMenu]);

  const columns = useMemo(
    () => [
      {
        header: "Sede",
        accessorKey: "sede",
        size: 100,
        cell: ({ row, getValue }) => {
          const sede = getValue();
          if (!sede) return '—';

          // Generar color único basado en hash del texto
          const stringToColor = (str) => {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
              hash = str.charCodeAt(i) + ((hash << 5) - hash);
            }

            const hue = Math.abs(hash % 360);
            const saturation = 65 + (Math.abs(hash >> 8) % 20); // 65-85%
            const lightness = 85 + (Math.abs(hash >> 16) % 10); // 85-95%

            return {
              bg: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
              color: `hsl(${hue}, ${saturation + 20}%, 30%)`,
              border: `hsl(${hue}, ${saturation}%, ${lightness - 15}%)`
            };
          };

          const colorStyle = stringToColor(sede);

          return (
            <div
              className="badge-cell"
              style={{
                backgroundColor: colorStyle.bg,
                color: colorStyle.color,
                border: `1px solid ${colorStyle.border}`
              }}
              onDoubleClick={() => { }}
            >
              <FaMapMarkerAlt className="badge-icon" style={{ color: colorStyle.color }} />
              <EditableCell
                value={sede}
                column={{ id: "sede" }}
                row={row}
                onSave={handleSave}
              />
            </div>
          );
        },
      },
      {
        header: "Ficha",
        accessorKey: "ficha",
        size: 80,
        cell: ({ row, getValue }) => (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            <EditableCell
              value={getValue()}
              column={{ id: "ficha" }}
              row={row}
              onSave={handleSave}
            />
          </span>
        ),
      },
      {
        header: "Foto",
        accessorKey: "foto",
        size: 56,
        maxSize: 56,
        cell: ({ row }) => {
          const foto = row.original.foto;

          return (
            <div
              className="employee-avatar"
              onClick={() => {
                setSelectedEmployeeForPhoto(row.original);
                setShowPhotoModal(true);
              }}
              style={{ cursor: 'pointer' }}
              title="Click para ver/editar foto y documentos"
            >
              {foto ? (
                <img
                  src={foto}
                  alt={row.original.nombre}
                  className="avatar-img"
                />
              ) : (
                <div className="avatar-placeholder">
                  <FaUser className="avatar-default" />
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: "Nombre",
        accessorKey: "nombre",
        size: 160,
        cell: ({ row, getValue }) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              className="editable-cell"
              style={{ cursor: "pointer" }}
              onDoubleClick={() => {
                setSelectedEmployeeForNameEdit(row.original);
                setShowEditNameModal(true);
              }}
              title="Doble click para editar nombre"
            >
              {row.original.primer_nombre && row.original.apellido
                ? `${row.original.primer_nombre} ${row.original.apellido}`
                : getValue() || '\u00A0'}
            </div>
            {Number(row.original.estado) === 1 && (() => {
              // En rojo si la persona ya ingresó y la ficha sigue abierta
              const ficha = estadoFicha(row.original);
              const detalle = [
                ficha.dias !== null ? `Abierta hace ${textoDias(ficha.dias)}` : null,
                ficha.yaIngreso
                  ? `ya ingresó (${fechaCorta(ficha.ingreso)})`
                  : ficha.ingreso ? `ingresa el ${fechaCorta(ficha.ingreso)}` : null,
              ].filter(Boolean).join(", ");
              return (
                <button
                  className={`badge-pendiente-btn${ficha.yaIngreso ? " is-vencida" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (puedeCorrerWizard) {
                      setEmployeeForWizard(row.original);
                    } else {
                      handleGenerarFichaITS(row.original);
                    }
                  }}
                  title={`${puedeCorrerWizard ? "Correr el wizard de Onboarding TI" : "Click para generar ficha TI"}${detalle ? `. ${detalle}` : ""}`}
                >
                  Ficha TI
                  {ficha.dias > 0 && <span className="badge-dias">{ficha.dias} d</span>}
                </button>
              );
            })()}
          </div>
        ),
      },
      {
        header: "Cédula",
        accessorKey: "cedula",
        size: 100,
        cell: ({ row, getValue }) => (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            <EditableCell
              value={getValue()}
              column={{ id: "cedula" }}
              row={row}
              onSave={handleSave}
            />
          </span>
        ),
      },
      {
        header: "Fecha de Ingreso",
        accessorKey: "fecha_ingreso",
        size: 130,
        cell: ({ row, getValue }) => (
          <EditableCell
            value={getValue()}
            column={{ id: "fecha_ingreso" }}
            row={row}
            onSave={handleSave}
          />
        )
      },
      {
        header: "Gerencia",
        accessorKey: "gerencia",
        size: 110,
        cell: ({ row, getValue }) => {
          const gerencia = getValue();
          if (!gerencia) return '—';

          // Generar color único basado en hash del texto (misma función que sede)
          const stringToColor = (str) => {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
              hash = str.charCodeAt(i) + ((hash << 5) - hash);
            }

            const hue = Math.abs(hash % 360);
            const saturation = 65 + (Math.abs(hash >> 8) % 20);
            const lightness = 85 + (Math.abs(hash >> 16) % 10);

            return {
              bg: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
              color: `hsl(${hue}, ${saturation + 20}%, 30%)`,
              border: `hsl(${hue}, ${saturation}%, ${lightness - 15}%)`
            };
          };

          const colorStyle = stringToColor(gerencia);

          return (
            <div
              className="badge-cell"
              style={{
                backgroundColor: colorStyle.bg,
                color: colorStyle.color,
                border: `1px solid ${colorStyle.border}`
              }}
              onDoubleClick={() => { }}
            >
              <FaBuilding className="badge-icon" style={{ color: colorStyle.color }} />
              <EditableCell
                value={gerencia}
                column={{ id: "gerencia" }}
                row={row}
                onSave={handleSave}
              />
            </div>
          );
        },
      },
      {
        header: "Departamento",
        accessorKey: "departamento",
        size: 120,
        cell: ({ row, getValue }) => (
          <EditableCell
            value={getValue()}
            column={{ id: "departamento" }}
            row={row}
            onSave={handleSave}
          />
        ),
      },
      {
        header: "Área",
        accessorKey: "area",
        size: 120,
        cell: ({ row, getValue }) => (
          <EditableCell
            value={getValue()}
            column={{ id: "area" }}
            row={row}
            onSave={handleSave}
          />
        ),
      },
      {
        header: "Cargo",
        accessorKey: "cargo",
        size: 150,
        cell: ({ row, getValue }) => {
          const detallesCargo = row.original.detalles_cargo;
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <EditableCell
                value={getValue()}
                column={{ id: "cargo" }}
                row={row}
                onSave={handleSave}
              />
              {detallesCargo && (
                <span style={{
                  fontSize: "0.85em",
                  color: "#1976d2",
                  fontStyle: "italic",
                  lineHeight: "1.2"
                }}>
                  {detallesCargo}
                </span>
              )}
            </div>
          );
        },
      },
      {
        header: "Equipo Asignado",
        accessorKey: "equipo_asignado_activo_fijo",
        id: "equipo_asignado",
        size: 180,
        cell: ({ row }) => {
          const equipoActivoFijo = row.original.equipo_asignado_activo_fijo;
          const equipoId = row.original.equipo_asignado_id;
          const computadora = row.original.computadora_asignada;

          if (!equipoId) return <span className="no-equipment">Sin equipo asignado</span>;
          if (!equipoActivoFijo) return <span className="no-equipment">Sin activo fijo</span>;

          const marca = computadora?.marca || '';
          const modelo = computadora?.modelo || '';
          const detalles = marca && modelo ? `(${marca} ${modelo})` : '';

          return (
            <span
              className="equipment-type"
              onClick={() => handleViewEquipment(equipoId)}
              title={`${equipoActivoFijo} ${detalles}`}
            >
              {computadora?.tipo?.toUpperCase() === "LAPTOP" ? (
                <FaLaptop
                  className="equipment-icon"
                  style={{ marginRight: "4px" }}
                />
              ) : computadora?.tipo?.toUpperCase() === "AIO" ||
                computadora?.tipo?.toUpperCase() === "DESKTOP" ? (
                <FaDesktop
                  className="equipment-icon"
                  style={{ marginRight: "4px" }}
                />
              ) : null}
              <span className="equipment-name" style={{ fontWeight: "bold" }}>
                {equipoActivoFijo}
              </span>
              {detalles && (
                <span style={{ fontSize: "0.85em", color: "#666", marginLeft: "4px" }}>
                  {detalles}
                </span>
              )}
            </span>
          );
        },
      },
      {
        header: "Smartphone Asignado",
        accessorKey: "smartphone_asignado_activo_fijo",
        id: "smartphone_asignado",
        size: 180,
        cell: ({ row }) => {
          const smartphoneActivoFijo = row.original.smartphone_asignado_activo_fijo;
          const smartphoneId = row.original.smartphone_asignado_id;
          const telefono = row.original.telefono_asignado;

          if (!smartphoneId) return <span className="no-equipment">Sin smartphone asignado</span>;

          const marca = telefono?.marca || '';
          const modelo = telefono?.modelo || '';
          const detalles = marca && modelo ? `(${marca} ${modelo})` : '';

          return (
            <span
              className="equipment-type"
              onClick={() => handleViewSmartphone(smartphoneId)}
              title={`${smartphoneActivoFijo} ${detalles}`}
            >
              <FaMobileAlt className="equipment-icon" style={{ marginRight: "4px" }} />
              <span className="equipment-name" style={{ fontWeight: "bold" }}>
                {smartphoneActivoFijo}
              </span>
              {detalles && (
                <span style={{ fontSize: "0.85em", color: "#666", marginLeft: "4px" }}>
                  {detalles}
                </span>
              )}
            </span>
          );
        },
      },
      {
        header: "Tablet Asignada",
        accessorKey: "tablet_asignada_activo_fijo",
        id: "tablet_asignada",
        size: 180,
        cell: ({ row }) => {
          const tablet = row.original.tablet_asignada;
          const tabletId = tablet?.id;
          const activoFijo = tablet?.activo_fijo;

          if (!tabletId) return <span className="no-equipment">Sin tablet asignada</span>;
          if (!activoFijo) return <span className="no-equipment">Sin activo fijo</span>;

          const marca = tablet?.marca || '';
          const modelo = tablet?.modelo || '';
          const detalles = marca && modelo ? `(${marca} ${modelo})` : '';

          return (
            <span
              className="equipment-type"
              onClick={() => handleViewTablet(tabletId)}
              title={`${activoFijo} ${detalles}`}
              style={{ cursor: "pointer" }}
            >
              <FaTabletAlt className="equipment-icon" style={{ marginRight: "4px" }} />
              <span className="equipment-name" style={{ fontWeight: "bold" }}>
                {activoFijo}
              </span>
              {detalles && (
                <span style={{ fontSize: "0.85em", color: "#666", marginLeft: "4px" }}>
                  {detalles}
                </span>
              )}
            </span>
          );
        },
      },
      {
        header: "Extensión",
        accessorKey: "extension",
        size: 100,
        cell: ({ row, getValue }) => (
          <EditableCell
            value={getValue()}
            column={{ id: "extension" }}
            row={row}
            onSave={handleSave}
          />
        ),
      },
      {
        header: "Línea",
        accessorKey: "linea",
        size: 120,
        cell: ({ row, getValue }) => (
          <EditableCell
            value={getValue()}
            column={{ id: "linea" }}
            row={row}
            onSave={handleSave}
          />
        ),
      },
      {
        header: "Correo",
        accessorKey: "correo",
        size: 180,
        cell: ({ row, getValue }) => (
          <EditableCell
            value={getValue()}
            column={{ id: "correo" }}
            row={row}
            onSave={handleSave}
          />
        ),
      },
      {
        header: "Acciones",
        accessorKey: "acciones",
        size: 140,
        maxSize: 140,
        enableSorting: false,
        cell: ({ row }) => (
          <div
            className={`action-buttons ${activeActionMenu === row.original.id ? "menu-active" : ""
              }`}
          >
            <button
              className="action-button view-button"
              onClick={() => handleViewEmployee(row.original)}
              aria-label="Ver expediente"
              title="Ver expediente"
            >
              <FaEllipsisH aria-hidden="true" />
            </button>

            <button
              className="action-button history-button"
              onClick={() => {
                setSelectedEmployeeForTimeline(row.original);
                setShowIndividualTimeline(true);
              }}
              aria-label="Ver historial"
              title="Ver historial"
            >
              <FaHistory aria-hidden="true" />
            </button>

            <div className="quick-actions-container" ref={quickActionsRef}>
              <button
                className={`icon-button ${activeActionMenu === row.original.id ? "active" : ""
                  }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (activeActionMenu === row.original.id) {
                    setActiveActionMenu(null);
                    setMenuAnchorRect(null);
                  } else {
                    setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
                    setActiveActionMenu(row.original.id);
                  }
                }}
                aria-label="Acciones rápidas"
                title="Acciones rápidas"
              >
                <FaBolt aria-hidden="true" />
              </button>
              <AnimatePresence>
                {activeActionMenu === row.original.id && (
                  <QuickActionsMenu anchorRect={menuAnchorRect}>
                    {[
                      ...(Number(row.original.estado) === 1 && puedeCorrerWizard
                        ? [{
                          icon: FaPlayCircle,
                          label: "Correr wizard",
                          color: "#D97706",
                          destacado: true,
                          onClick: () => {
                            setEmployeeForWizard(row.original);
                            setActiveActionMenu(null);
                          }
                        }]
                        : []),
                      // Con la ficha ya cerrada el badge desaparece: así se vuelve al wizard
                      ...(Number(row.original.estado) !== 1 && row.original.tiene_onboarding && puedeCorrerWizard
                        ? [{
                          icon: FaClipboardList,
                          label: "Ver onboarding",
                          color: "#D97706",
                          onClick: () => {
                            setEmployeeForWizard(row.original);
                            setActiveActionMenu(null);
                          }
                        }]
                        : []),
                      {
                        icon: FaNetworkWired,
                        label: "Cuentas",
                        color: "#8B5CF6",
                        onClick: () => {
                          setCuentaRedDe(row.original);
                          setActiveActionMenu(null);
                        }
                      },
                      {
                        icon: FaAddressCard,
                        label: "Copiar contacto",
                        color: "#0EA5E9",
                        onClick: () => {
                          copiarContacto(row.original);
                          setActiveActionMenu(null);
                        }
                      },
                      {
                        icon: FaSignature,
                        label: "Generar firma",
                        color: "#F59E0B",
                        onClick: () => {
                          setSelectedEmployeeForFirma(row.original);
                          setShowGenerarFirmaModal(true);
                          setActiveActionMenu(null);
                        }
                      },
                      {
                        icon: FaHandshake,
                        label: "Generar bienvenida",
                        color: "#EC4899",
                        onClick: () => {
                          setSelectedEmployeeForBienvenida(row.original);
                          setShowGenerarBienvenidaModal(true);
                          setActiveActionMenu(null);
                        }
                      },
                      // Lo menos frecuente, al final
                      ...(puedeMoverPersonal && row.original.activo !== false
                        ? [{
                          icon: FaExchangeAlt,
                          label: "Movimiento interno",
                          color: "#059669",
                          onClick: () => {
                            setMovimientoDe(row.original);
                            setActiveActionMenu(null);
                          }
                        }]
                        : []),
                    ].map((action) => (
                      <button
                        key={action.label}
                        className={`quick-action-item${action.destacado ? " is-destacado" : ""}`}
                        onClick={action.onClick}
                      >
                        <span className="action-icon" style={{ color: action.color }}>
                          <action.icon />
                        </span>
                        <span className="action-label">{action.label}</span>
                      </button>
                    ))}
                  </QuickActionsMenu>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={() => handleDelete(row.original)}
              className="action-button delete-button"
              aria-label="Eliminar empleado"
              title="Eliminar empleado"
            >
              <FaTimes aria-hidden="true" />
            </button>
          </div>
        ),
      },
    ],
    [activeActionMenu, puedeCorrerWizard, puedeMoverPersonal]
  );

  const availableFilters = useMemo(() => {
    const gerenciaFilter = {
      id: "gerencia",
      placeholder: "Todas las gerencias",
    };

    const departamentoFilter = {
      id: "departamento",
      placeholder: "Todos los departamentos",
      options: departamentos
        .filter(d => {
          if (!filters.gerencia) return true;
          const gerencia = gerencias.find(g => g.nombre === filters.gerencia);
          return gerencia && d.gerencia_id === gerencia.id;
        })
        .map(d => d.nombre)
    };

    const areaFilter = {
      id: "area",
      placeholder: "Todas las áreas",
      options: areas
        .filter(a => {
          if (!filters.departamento) return true;
          const departamento = departamentos.find(d => d.nombre === filters.departamento);
          return departamento && a.departamento_id === departamento.id;
        })
        .map(a => a.nombre)
    };

    return [
      {
        id: "sede",
        placeholder: "Todas las sedes",
      },
      gerenciaFilter,
      departamentoFilter,
      areaFilter,
    ];
  }, [filters, gerencias, departamentos, areas]);

  useEffect(() => {
    // Defer data fetching to allow page transition animations to finish smoothly
    const timer = setTimeout(() => {
      fetchData();
      fetchFilterOptions();
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const [deptosRes, gerenciasRes, areasRes] = await Promise.all([
        axiosInstance.get('/api/departamentos'),
        axiosInstance.get('/api/gerencias'),
        axiosInstance.get('/api/areas')
      ]);
      setDepartamentos(deptosRes.data);
      setGerencias(gerenciasRes.data);
      setAreas(areasRes.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/empleados");
      const jsonData = response.data;

      // Procesar los datos para incluir la información del smartphone
      const processedData = jsonData.map((empleado) => ({
        ...empleado,
        smartphone_asignado: empleado.sp_asignado
          ? empleado.sp_asignado.id
          : null,
        smartphone_modelo: empleado.sp_asignado
          ? `${empleado.sp_asignado.marca} ${empleado.sp_asignado.modelo}`
          : null,
        linea: empleado.linea || null
      }));

      setData(processedData);
      setError(null);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Error al cargar los datos. Por favor, intente de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (id, field, value, displayName) => {
    try {
      // Asegurarnos de que cargo_id se envíe como cargo_area_id
      const fieldToSend = field === 'cargo_id' ? 'cargo_area_id' : field;

      const response = await axiosInstance.patch(`/api/empleados/${id}`, {
        [fieldToSend]: value
      });

      if (response.data.message) {
        setData((oldData) =>
          oldData.map((row) => {
            if (row.id === id) {
              if (field.endsWith('_id') && displayName) {
                return {
                  ...row,
                  [field]: value,
                  [field.replace('_id', '')]: displayName,
                };
              }
              return { ...row, [field]: value };
            }
            return row;
          })
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error in handleSave:", error);
      return false;
    }
  };

  const handleSaveNameEdit = async (id, nameData) => {
    try {
      const response = await axiosInstance.patch(`/api/empleados/${id}`, nameData);

      if (response.data.message) {
        // Como el backend: solo primer nombre y primer apellido (los segundos son referenciales)
        const nombreDisplay = [nameData.nombre, nameData.apellido].filter(Boolean).join(' ');

        setData((oldData) =>
          oldData.map((row) => {
            if (row.id === id) {
              return {
                ...row,
                nombre: nombreDisplay,
                primer_nombre: nameData.nombre,
                segundo_nombre: nameData.segundo_nombre,
                apellido: nameData.apellido,
                segundo_apellido: nameData.segundo_apellido
              };
            }
            return row;
          })
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error al guardar nombre:", error);
      throw error;
    }
  };

  const handleDelete = (employee) => {
    setSelectedEmployeeForDelete(employee);
    setShowDeleteModal(true);
  };

  // El modal sigue abierto despues del egreso: ahi se ven el resultado y el
  // cierre de sus cuentas. Si falla, el error vuelve al modal.
  const handleConfirmDelete = async (deleteData) => {
    const id = selectedEmployeeForDelete.id;
    const response = await axiosInstance.delete(`/api/empleados/${id}`, { data: deleteData });
    setData((prevData) => prevData.filter((e) => e.id !== id));
    fetchData();
    return response.data;
  };

  const handleEmployeeAdded = () => {
    // Refetch para traer la fila con todos los campos (necesita_*, foto,
    // equipos, jerarquia, estado numérico). Inyectar el parcial del POST
    // dejaba al empleado nuevo sin "Ficha TI pendiente" hasta refrescar.
    fetchData();
    setShowNewEmployeeModal(false);
  };

  const handleViewEquipment = async (equipoAsignado) => {
    try {
      const response = await axiosInstance.get(
        `/api/activos/buscar?nombre=${encodeURIComponent(equipoAsignado)}`
      );
      setSelectedAsset(response.data);
      setShowAssetModal(true);
    } catch (error) {
      console.error("Error al obtener detalles del equipo:", error);
    }
  };

  const handleViewSmartphone = async (smartphoneId) => {
    try {
      const response = await axiosInstance.get(`/api/smartphones/${smartphoneId}`);
      setSelectedAsset(response.data);
      setShowSmartphoneModal(true);
    } catch (error) {
      console.error("Error al obtener detalles del smartphone:", error);
    }
  };

  const handleViewTablet = async (tabletId) => {
    try {
      const response = await axiosInstance.get(`/api/tablets/${tabletId}`);
      setSelectedAsset(response.data);
      setShowTabletModal(true);
    } catch (error) {
      console.error("Error al obtener detalles de la tablet:", error);
    }
  };

  // El expediente carga sus propios datos
  const handleViewEmployee = (empleado) => {
    setSelectedEmployee({ id: empleado.id });
  };

  const copiarContacto = async (empleado) => {
    const texto = [
      empleado.nombre,
      [empleado.cargo, empleado.detalles_cargo].filter(Boolean).join(", "),
      empleado.correo,
      empleado.extension && `Ext. ${empleado.extension}`,
      empleado.linea && `Línea ${empleado.linea}`,
    ].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(texto);
    } catch {
      alert(texto);
    }
  };

  useEffect(() => {
    const empleadoId = Number(searchParams.get('id'));
    if (!empleadoId || selectedEmployee?.id === empleadoId || data.length === 0) return;

    // Si no está en la lista (egresado), igual se abre: el expediente se carga solo
    const empleado = data.find(item => item.id === empleadoId);
    handleViewEmployee(empleado || { id: empleadoId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, data, selectedEmployee?.id]);

  // Desde el aviso de egreso o de movimiento: /empleados?cierre=<id>[&tipo=movimiento]
  // abre el cierre de sus cuentas (el egresado ya no está en la lista)
  useEffect(() => {
    const empleadoId = Number(searchParams.get('cierre'));
    if (!empleadoId || !puedeCorrerWizard) return;
    setCierreDe({ id: empleadoId, tipo: searchParams.get('tipo') === 'movimiento' ? 'movimiento' : 'egreso' });
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('cierre');
    nextParams.delete('tipo');
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, puedeCorrerWizard]);

  // Desde la notificación de Ficha TI: /empleados?onboarding=<id> abre el wizard
  useEffect(() => {
    const empleadoId = Number(searchParams.get('onboarding'));
    if (!empleadoId || !puedeCorrerWizard || data.length === 0) return;
    const empleado = data.find(item => item.id === empleadoId);
    if (empleado) setEmployeeForWizard(empleado);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('onboarding');
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, data, puedeCorrerWizard]);

  const getRowClassName = useCallback((row) => {
    return Number(row.original.estado) === 1 ? "empleado-pendiente" : "";
  }, []);

  const handleGenerarFichaITS = (employee) => {
    setEmployeeForApproval(employee);
    setShowApprovalModal(true);
  };

  const handleUpdateEmployee = async () => {
    try {
      // Recargar los datos del empleado
      const response = await axiosInstance.get(`/api/empleados/${employeeForApproval.id}`);
      const updatedEmployee = response.data;

      // Actualizar el estado local
      setData(prevData => {
        return prevData.map(emp =>
          emp.id === employeeForApproval.id ? updatedEmployee : emp
        );
      });

      // Actualizar el empleado en el modal
      setEmployeeForApproval(updatedEmployee);

      return updatedEmployee; // Retornar los datos actualizados

    } catch (error) {
      console.error('Error en handleUpdateEmployee:', error);
      throw error; // Propagar el error para manejarlo en el componente hijo
    }
  };

  const handleConfirmGenerarFicha = async () => {
    try {
      const response = await axiosInstance.patch(
        `/api/empleados/${employeeForApproval.id}`,
        {
          estado: 0, // Cambiando estado de 1 (pendiente) a 0 (activo)
        }
      );

      if (response.data.message) {
        // Actualizar datos localmente
        setData((prevData) =>
          prevData.map((emp) =>
            emp.id === employeeForApproval.id ? { ...emp, estado: 0 } : emp
          )
        );
        setShowApprovalModal(false);
        setEmployeeForApproval(null);
        alert("Ficha TI generada exitosamente");
      }
    } catch (error) {
      console.error("Error al generar ficha TI:", error);
      alert(error.response?.data?.error || "Error al generar la ficha TI");
    }
  };

  const handlePhotoUpload = async (employeeId, file, type) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type); // foto, cedula, o rif

      // Obtener la ficha del empleado
      const employee = data.find(emp => emp.id === employeeId);
      if (employee && employee.ficha) {
        formData.append('ficha', employee.ficha);
      }

      const response = await axiosInstance.post(
        `/api/empleados/${employeeId}/adjuntos`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data) {
        // Recargar los datos del empleado para obtener los adjuntos actualizados
        fetchData();
        alert(`Archivo subido exitosamente`);
        return true;
      }
    } catch (error) {
      console.error(`Error subiendo archivo:`, error);
      alert(`Error al subir archivo: ${error.response?.data?.error || error.message}`);
      return false;
    }
  };

  const handleExportToCSV = () => {
    try {
      // Preparamos los datos para CSV, solo usando las columnas visibles
      const exportColumns = columns
        .filter(col => {
          const colId = col.id || col.accessorKey;
          // Excluir acciones y foto
          if (colId === 'acciones' || colId === 'foto') return false;
          // Incluir solo si está visible
          return columnVisibility[colId];
        })
        .map(col => ({
          key: col.accessorKey || col.id,
          header: typeof col.header === 'string' ? col.header : col.accessorKey || col.id
        }));

      // Creamos el encabezado del CSV
      let csvContent = exportColumns.map(col => `"${col.header}"`).join(',') + '\n';

      // Usar las filas filtradas/ordenadas por TableView; fallback a data completa
      const rowsToExport = filteredRows.length > 0 ? filteredRows : data;

      // Añadimos los datos de cada fila
      rowsToExport.forEach(row => {
        const rowData = exportColumns.map(col => {
          let value = '';

          // Manejo especial para equipos asignados
          if (col.key === 'equipo_asignado_activo_fijo' && row.computadora_asignada) {
            const eq = row.computadora_asignada;
            value = `${eq.activo_fijo} (${eq.marca} ${eq.modelo})`;
          }
          // Manejo especial para smartphones asignados
          else if (col.key === 'smartphone_asignado_activo_fijo' && row.telefono_asignado) {
            const sp = row.telefono_asignado;
            value = `${sp.activo_fijo} (${sp.marca} ${sp.modelo})`;
          }
          // Manejo especial para tablets asignadas
          else if (col.key === 'tablet_asignada_activo_fijo' && row.tablet_asignada) {
            const tb = row.tablet_asignada;
            value = `${tb.activo_fijo} (${tb.marca} ${tb.modelo})`;
          }
          // Valor normal para otras columnas
          else {
            value = row[col.key] !== null && row[col.key] !== undefined ? row[col.key] : '';
          }

          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvContent += rowData.join(',') + '\n';
      });

      // BOM UTF-8 para que Excel reconozca acentos al abrir el archivo
      const blob = new Blob(['﻿', csvContent], { type: 'text/csv;charset=utf-8;' });

      // Creamos un enlace para descargar el archivo
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      const fecha = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `Empleados_${fecha}.csv`);
      link.style.visibility = 'hidden';

      // Añadimos el enlace al DOM, hacemos clic y lo eliminamos
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error al exportar a CSV:", error);
      alert("Error al exportar a CSV. Por favor, inténtelo de nuevo.");
    }
  };

  return (
    <div className="employees-view">
      {/* Header con Tailwind */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
        <h2 className="page-title">Empleados {`(${filteredCount})`}</h2>
        <div className="flex gap-3 items-center">
          <button
            className="btn-secondary"
            onClick={() => setShowTimeline(true)}
          >
            <FaHistory />
            Historial
          </button>
          {puedeCorrerWizard && (
            <button
              className="btn-secondary"
              onClick={() => setCierreDe({ id: null, tipo: 'egreso' })}
              title="Lo que le quedó abierto a alguien que ya egresó"
            >
              <FaUserLock />
              Cuentas de egresados
            </button>
          )}
          <button
            className="btn-primary"
            onClick={() => setShowNewEmployeeModal(true)}
          >
            <FaUserPlus /> Registrar ingreso
          </button>
        </div>
      </div>

      <TableView
        data={data}
        columns={columns}
        loading={loading}
        error={error}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        defaultPageSize={30}
        defaultSorting={[{ id: 'ficha', desc: true }]}
        activeActionMenu={activeActionMenu}
        setActiveActionMenu={setActiveActionMenu}
        availableFilters={availableFilters}
        onFilteredCountChange={setFilteredCount}
        onFilteredRowsChange={setFilteredRows}
        onFiltersChange={handleFilterChange}
      />

      <div className="flex justify-end mt-4">
        <button
          className="btn-secondary"
          onClick={handleExportToCSV}
          disabled={loading || data.length === 0}
          title={
            filteredCount > 0 && filteredCount < data.length
              ? `Exporta los ${filteredCount} empleados visibles tras aplicar filtros`
              : 'Exporta todos los empleados'
          }
        >
          <FaDownload /> Exportar a CSV
          {filteredCount > 0 && filteredCount < data.length && ` (${filteredCount})`}
        </button>
      </div>

      {selectedEmployee && (
        <ExpedienteEmpleado
          key={selectedEmployee.id}
          empleadoId={selectedEmployee.id}
          onClose={() => {
            setSelectedEmployee(null);
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('id');
            setSearchParams(nextParams, { replace: true });
          }}
          onCambio={fetchData}
          onCerrarCuentas={puedeCorrerWizard ? (id) => setCierreDe({ id, tipo: 'egreso' }) : undefined}
        />
      )}

      {showNewEmployeeModal && (
        <NewEmployeeModal
          onClose={() => setShowNewEmployeeModal(false)}
          onEmployeeAdded={handleEmployeeAdded}
        />
      )}

      {showAssetModal && selectedAsset && (
        <AssetModal
          asset={selectedAsset}
          onClose={() => {
            setShowAssetModal(false);
            setSelectedAsset(null);
          }}
        />
      )}

      {showSmartphoneModal && selectedAsset && (
        <SmartphoneModal
          smartphone={selectedAsset}
          onClose={() => {
            setShowSmartphoneModal(false);
            setSelectedAsset(null);
          }}
        />
      )}

      {showTabletModal && selectedAsset && (
        <TabletModal
          tablet={selectedAsset}
          onClose={() => {
            setShowTabletModal(false);
            setSelectedAsset(null);
          }}
        />
      )}

      {showTimeline && (
        <TimelineView
          categoria="empleados"
          onClose={() => setShowTimeline(false)}
        />
      )}

      {showIndividualTimeline && selectedEmployeeForTimeline && (
        <TimelineView
          categoria="empleados"
          itemId={selectedEmployeeForTimeline.id}
          itemName={selectedEmployeeForTimeline.nombre}
          onClose={() => {
            setShowIndividualTimeline(false);
            setSelectedEmployeeForTimeline(null);
          }}
        />
      )}

      {cuentaRedDe && (
        <CuentasModal
          empleado={cuentaRedDe}
          puedeGestionar={puedeCorrerWizard}
          onClose={() => setCuentaRedDe(null)}
        />
      )}

      {movimientoDe && (
        <MovimientoInternoModal
          empleado={movimientoDe}
          onClose={() => setMovimientoDe(null)}
          onDone={(mensaje) => {
            setMovimientoDe(null);
            alert(mensaje);
            fetchData();
          }}
        />
      )}

      {showGenerarFirmaModal && (
        <GenerarFirmaModal
          isOpen={showGenerarFirmaModal}
          onClose={() => {
            setShowGenerarFirmaModal(false);
            setSelectedEmployeeForFirma(null);
          }}
          employee={{
            nombre: selectedEmployeeForFirma?.nombre,
            cargo:
              selectedEmployeeForFirma?.cargo_area?.cargo_base?.nombre ||
              selectedEmployeeForFirma?.cargo,
            extension: selectedEmployeeForFirma?.extension,
          }}
        />
      )}

      {showGenerarBienvenidaModal && (
        <GenerarBienvenidaModal
          isOpen={showGenerarBienvenidaModal}
          onClose={() => {
            setShowGenerarBienvenidaModal(false);
            setSelectedEmployeeForBienvenida(null);
          }}
          employee={selectedEmployeeForBienvenida}
        />
      )}

      {cierreDe && (
        <CierreCuentasModal
          key={`${cierreDe.id || 'buscar'}-${cierreDe.tipo}`}
          empleadoId={cierreDe.id}
          tipo={cierreDe.tipo}
          onClose={() => setCierreDe(null)}
        />
      )}

      {showDeleteModal && selectedEmployeeForDelete && (
        <DeleteEmployeeModal
          employee={selectedEmployeeForDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedEmployeeForDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          puedeGestionarCuentas={puedeCorrerWizard}
        />
      )}

      {showApprovalModal && employeeForApproval && (
        <FichaITSModal
          employee={employeeForApproval}
          onClose={() => {
            setShowApprovalModal(false);
            setEmployeeForApproval(null);
          }}
          onConfirm={handleConfirmGenerarFicha}
          onUpdate={handleUpdateEmployee}
        />
      )}

      {employeeForWizard && (
        <OnboardingWizard
          employee={employeeForWizard}
          onClose={async () => {
            const id = employeeForWizard.id;
            setEmployeeForWizard(null);
            // El wizard pudo cambiar correo, equipos o el estado de la ficha
            try {
              const response = await axiosInstance.get(`/api/empleados/${id}`);
              // El detalle no trae todo lo del listado: se mezcla en vez de reemplazar
              setData((prevData) => prevData.map((emp) => (emp.id === id ? { ...emp, ...response.data, linea: response.data.linea || null, tiene_onboarding: true } : emp)));
            } catch (error) {
              console.error("No se pudo refrescar el empleado:", error);
            }
          }}
          onFichaCerrada={(id) => {
            setData((prevData) => prevData.map((emp) => (emp.id === id ? { ...emp, estado: 0 } : emp)));
          }}
        />
      )}

      {showPhotoModal && selectedEmployeeForPhoto && (
        <EmployeePhotoModal
          employee={selectedEmployeeForPhoto}
          onClose={() => {
            setShowPhotoModal(false);
            setSelectedEmployeeForPhoto(null);
          }}
          onPhotoUpload={handlePhotoUpload}
        />
      )}

      {showEditNameModal && selectedEmployeeForNameEdit && (
        <EditNameModal
          isOpen={showEditNameModal}
          onClose={() => {
            setShowEditNameModal(false);
            setSelectedEmployeeForNameEdit(null);
          }}
          employee={selectedEmployeeForNameEdit}
          onSave={handleSaveNameEdit}
        />
      )}
    </div>
  );
}

export default EmployeesView;
